import React from 'react';
import { GROW_MARK, GROW_MARK_BIG_OUTLINE } from '../GrowLogo';
import { EQ } from './TriGlyph';
import './Triangles.css';

export type RiseVariant = 'filled' | 'steps' | 'missing' | 'animated' | 'loading';

/** The mark's own box inside GROW_MARK's 64 x 64, with 1 unit to spare for an outline. */
const MARK_BOX = { x: 3.48, y: 17.85, w: 57.04, h: 28.3 };

/** Step glyphs: heights from 1/2 to 1 of the tallest (the Rise's 2 : 4), a gap of 0.14 between. */
const STEP_H = 100;
const STEP_GAP = 14;
/** Half the outline's width, in step units (the tallest step is 100). */
const STROKE = 3.5;

export interface StepGeometry { x: number; h: number; w: number }

/** N rising, equilateral glyphs on one baseline: one deliberate progression, small to big. */
export function stepGeometry(steps: number): { tris: StepGeometry[]; width: number } {
  const n = Math.max(1, Math.round(steps));
  const tris: StepGeometry[] = [];
  let x = 0;
  for (let i = 0; i < n; i += 1) {
    const h = n === 1 ? STEP_H : STEP_H * (0.5 + (0.5 * i) / (n - 1));
    const w = h / EQ;
    tris.push({ x, h, w });
    x += w + STEP_GAP;
  }
  return { tris, width: x - STEP_GAP };
}

export interface RiseMarkProps {
  /**
   * filled: the Rise. steps: N glyphs, the first `reached` filled, the rest outlined.
   * missing: the big one is only an outline (the 404). animated: rises once, 120ms apart
   * (a course finished, a target reached). loading: rises in turn while something loads.
   */
  variant?: RiseVariant;
  /** The mark's height: a number is px, a string any CSS length (default 1em). */
  size?: number | string;
  /** steps: how many glyphs, and how many are reached. */
  steps?: number;
  reached?: number;
  /** Read out as an image ("Level 2 of 5"); without it the mark is decorative. */
  label?: string;
  className?: string;
}

/**
 * Grow's own gesture (DESIGN_SYSTEM.md "Triangles in Grow"): use it only where something grows.
 * The mark variants take the logo's three tints; steps take one colour (currentColor).
 */
export function RiseMark({ variant = 'filled', size = '1em', steps = 3, reached = 0, label, className }: RiseMarkProps) {
  const height = typeof size === 'number' ? `${size}px` : size;
  const a11y = label
    ? { role: variant === 'loading' ? 'status' : 'img', 'aria-label': label }
    : { 'aria-hidden': true as const };
  const classes = ['ui-rise', `ui-rise--${variant}`, className].filter(Boolean).join(' ');

  if (variant === 'steps') {
    const { tris, width } = stepGeometry(steps);
    const pad = 1;
    const vbW = width + pad * 2;
    const vbH = STEP_H + pad * 2;
    return (
      <svg
        className={classes}
        viewBox={`${-pad} ${-pad} ${vbW} ${vbH}`}
        style={{ height, width: `calc(${height} * ${(vbW / vbH).toFixed(4)})` }}
        focusable="false"
        data-testid="rise-mark"
        data-variant={variant}
        {...a11y}
      >
        {tris.map((t, i) => {
          const on = i < reached;
          // An outline is drawn inside its triangle (shrunk about the centroid by half the
          // stroke), so a reached and an open step are the same size.
          const k = on ? 1 : 1 - (STROKE * 3) / t.h;
          const cx = t.x + t.w / 2;
          const cy = STEP_H - t.h / 3;
          const p = (x: number, y: number) => `${(cx + (x - cx) * k).toFixed(2)},${(cy + (y - cy) * k).toFixed(2)}`;
          return (
            <polygon
              key={i}
              points={`${p(t.x, STEP_H)} ${p(t.x + t.w, STEP_H)} ${p(cx, STEP_H - t.h)}`}
              className={on ? 'ui-rise__step ui-rise__step--on' : 'ui-rise__step'}
              strokeWidth={on ? undefined : STROKE * 2}
              data-reached={on ? 'true' : 'false'}
            />
          );
        })}
      </svg>
    );
  }

  const parts = variant === 'missing' ? GROW_MARK.slice(0, 2) : GROW_MARK;
  return (
    <svg
      className={classes}
      viewBox={`${MARK_BOX.x} ${MARK_BOX.y} ${MARK_BOX.w} ${MARK_BOX.h}`}
      style={{ height, width: `calc(${height} * ${(MARK_BOX.w / MARK_BOX.h).toFixed(4)})` }}
      focusable="false"
      data-testid="rise-mark"
      data-variant={variant}
      {...a11y}
    >
      {parts.map((d, i) => (
        <path key={d} d={d} className="ui-rise__part" style={{ ['--ui-rise-i' as any]: i }} />
      ))}
      {variant === 'missing' && (
        <path d={GROW_MARK_BIG_OUTLINE} className="ui-rise__gone" vectorEffect="non-scaling-stroke" data-testid="rise-missing" />
      )}
    </svg>
  );
}
